import type { CategoryProgress, CategoryThresholdProgress, ReflectionInviteState, ReflectionResponseRelationship } from "~vue/api/generated/reflections";

type NominationInvite = { state?: ReflectionInviteState | null; relationship?: ReflectionResponseRelationship | null };

// Mirrors the server's nomination count: an invite is a live nomination until
// it is revoked or expires.
const NOMINATED_INVITE_STATES: ReflectionInviteState[] = ["pending", "responded"];

// An org that anonymizes rater status gets invites with no `state` at all, and
// the server has already dropped the ones that aren't live before sending them.
// Filtering on the missing state would zero every category instead.
function isLiveNomination(invite: NominationInvite): boolean {
    return !invite.state || NOMINATED_INVITE_STATES.includes(invite.state);
}

// The cycle payload snapshots `nominated` at fetch time, but the subject adds
// and removes raters without refetching it. Recount from the list on screen so
// the thresholds and the launch gate track what the subject just did.
export function withLiveNominations(progress: CategoryThresholdProgress, invites: NominationInvite[]): CategoryThresholdProgress {
    const counts = new Map<string, number>();
    for (const invite of invites) {
        if (invite.relationship && isLiveNomination(invite)) {
            counts.set(invite.relationship, (counts.get(invite.relationship) ?? 0) + 1);
        }
    }
    return {
        ...progress,
        categories: progress.categories.map((category) => ({ ...category, nominated: counts.get(category.value) ?? 0 })),
    };
}

export function submittedProgressLabel(progress: CategoryThresholdProgress): string {
    return progress.categories.map((category) => `${category.label} ${category.submitted}/${category.min_responses}`).join(" · ");
}

// One category clearing its threshold unlocks the report, so the closest
// category is the honest measure of how far along the subject is.
export function submittedProgressPct(progress: CategoryThresholdProgress): number {
    const ratios = progress.categories.map((category) => Math.min(1, category.submitted / category.min_responses));
    if (ratios.length === 0) {
        return 0;
    }
    return Math.round(Math.max(...ratios) * 100);
}

// What still stands between the subject and their report. One category clearing
// its threshold is enough, so the categories read as alternatives, not a list to
// work through. Returns null once nothing is outstanding.
export function reportShortfallCopy(progress: CategoryThresholdProgress): string | null {
    const outstanding: string[] = [];
    if (progress.self_assessment_required && !progress.self_assessment_submitted) {
        outstanding.push("complete your self reflection");
    }
    if (!progress.thresholds_met) {
        const options = progress.categories
            .filter((category) => !category.met)
            .map((category) => {
                const missing = category.min_responses - category.submitted;
                return `${missing} more ${category.label} ${missing === 1 ? "response" : "responses"}`;
            })
            .join(", or ");
        outstanding.push(`collect ${options}`);
    }
    if (outstanding.length === 0) {
        return null;
    }
    return `To unlock your report you still need to ${outstanding.join(", and ")}.`;
}

// The headline rule, stated the way the assessment's own guidance states it.
export function reportEligibilityCopy(progress: CategoryThresholdProgress): string {
    const self = progress.self_assessment_required ? "complete a self-assessment AND " : "";
    return `You will ONLY be eligible to receive an assessment report if you ${self}get enough feedback in at least ONE feedback category.`;
}

// Whether the subject has invited enough raters into a category. The report
// still turns on submitted responses, so this marks the nomination step done,
// not the threshold itself.
export function nominationMet(category: CategoryProgress): boolean {
    return category.nominated >= category.min_responses;
}

// One threshold as a bullet: "Manager: 1 submitted response".
export function thresholdRequirementLabel(category: CategoryProgress): string {
    const responses = category.min_responses === 1 ? "1 submitted response" : `${category.min_responses} or more responses`;
    return `${category.label}: ${responses}`;
}
